import {DataModificationsHeader} from 'audit/DataModifications';
import {SpreadsheetValidationErrorInfo} from 'bulk-import/SpreadsheetBulkImportConstants';
import {Grid, GridSummary, ParentStorableData, Storable, StorableSummary} from 'constants/SampleConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {MinimalSchemaTag, SchemaTag} from 'folder-items/FileConstants';
import {
  GridCoordinatesDirections,
  PrimaryExperimentalRoleType,
  SortDirection,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExportToCSVResponse} from 'samples/ContainerDAO';
import {ExperimentalRole} from 'samples/experimental-roles/ExperimentalRoleConstants';
import {Change} from 'samples/grids/upload-containers/UploadContainersFlowConstants';
import {WellPosition, MeasurementInput} from 'samples/plate-maps/PlateMapConstants';
import {TagUpdateObject} from 'schemas/tags/TagUtil';
import APIUtil from 'util/APIUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export interface FetchAllGridsResponse {
  grids: Array<GridSummary>;
  limit: number;
  total: number;
}

export interface FetchGridResponse {
  grid: Grid;
  parentStorable: StorableSummary | null;
}

export function fetch(gridId: string): Promise<FetchGridResponse> {
  return net.getJSONPromise(`/1/api/grids/${gridId}`);
}

export interface CreateGridParams {
  barcode?: string;
  containerSchemaId?: string;
  locationId?: string;
  parentStorableData?: ParentStorableData;
  sourceId?: string | null;
  spreadsheetName?: string;
  spreadsheetURL?: string | null;
  tags?: TagUpdateObject;
  studyIds?: Array<string>;
}

export function createGrid(
  gridSchemaId: string,
  name: string,
  data: CreateGridParams = {}
): JQueryPromise<GridSummary> {
  return net.postJSON({
    url: '/1/api/grids',
    data: {gridSchemaId, name, ...data},
  });
}

export interface UpdateGridParams {
  name?: string;
  barcode?: string;
  parentStorableData?: ParentStorableData;
  sourceId?: string;
  tags?: {[tagName: string]: Partial<SchemaTag>};
}

export interface UpdateGridResponse {
  grid: GridSummary;
  parentStorable: Storable | null;
}

export function updateGrid(
  gridId: string,
  folderId: string | null,
  data: UpdateGridParams,
  dataModsHeader?: DataModificationsHeader
): Promise<UpdateGridResponse> {
  return net
    .patchJSONPromise({url: `/1/api/grids/${gridId}`, data, extraHeaders: dataModsHeader})
    .then((response) => {
      FolderDAOCache.invalidateFolder(folderId);
      return response;
    });
}

export function exportToCSV(gridIds: Array<string>): Promise<ExportToCSVResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/grids:export-to-csv',
    data: {
      gridIds,
      // TODO(BNCH-104461): Pass in current sort settings from UI.
      sortDirection: SortDirection.ASCENDING,
      gridDirection: GridCoordinatesDirections.ACROSS_ROWS,
    },
  });
}

export interface ValidateUploadContainersFromSpreadsheetResponse {
  errors: SpreadsheetValidationErrorInfo;
  gridPreview: Grid;
  changes: Array<Change>;
}

export function validateUploadContainersFromSpreadsheet(
  gridId: string,
  containerSchemaId: string,
  containerSourceId: string,
  spreadsheetName: string,
  spreadsheetURL: string,
  spreadsheetFormat: string
): JQueryPromise<ValidateUploadContainersFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    data: {
      containerSchemaId,
      containerSourceId,
      spreadsheetName,
      spreadsheetURL,
      spreadsheetFormat,
    },
    url: `/1/api/grids/${gridId}:validate-upload-containers-from-spreadsheet`,
  });
}

export function uploadContainersFromSpreadsheet(
  gridId: string,
  containerSchemaId: string,
  containerSourceId: string,
  spreadsheetName: string,
  spreadsheetURL: string,
  spreadsheetFormat: string,
  orphanParentStorableDatas: Array<ParentStorableData>,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{}> {
  return net.postJSON({
    url: `/1/api/grids/${gridId}:upload-containers-from-spreadsheet`,
    data: {
      containerSchemaId,
      containerSourceId,
      spreadsheetName,
      spreadsheetURL,
      spreadsheetFormat,
      orphanParentStorableDatas,
    },
    extraHeaders: dataModsHeader,
  });
}

export function validateWellSpreadsheet(
  gridId: string,
  gridSchemaId: string,
  containerSchemaId: string,
  spreadsheetName: string,
  spreadsheetURL: string
): JQueryPromise<{}> {
  return net.postJSON({
    url: '/1/api/grids/actions/validate-well-spreadsheet',
    data: {gridId, gridSchemaId, containerSchemaId, spreadsheetName, spreadsheetURL},
  });
}

export function gridEditorURL(gridId: string): string {
  // TODO(T9772): Remove once grids have been migrated over to boxes and plates
  return `/samples/grids/${APIUtil.unprefixApiId(gridId)}`;
}

export function boxEditorURL(boxId: string): string {
  return `/samples/boxes/${boxId}`;
}

export function plateEditorURL(plateId: string): string {
  return `/samples/plates/${plateId}`;
}

// Keep in sync with benchling/inventory/fixed_plate/domain/fixed_plate_input_types.py
// Keys are in snake case to be compatible with what the backend expects.
// These types should only be used to prepare the API payload for plate transfers.
interface ExperimentalRoleInput extends Omit<ExperimentalRole, 'primaryRole'> {
  primary_role: PrimaryExperimentalRoleType;
}

export interface ProcedureParameterValue {
  parameter_version_id: string;
  schema_field_value_dict: Partial<MinimalSchemaTag>;
}

export interface PlateMapTestOrderInput {
  test_order_id: string;
}

export interface AnnotateFixedPlateWellInput {
  position: WellPosition;
  role: ExperimentalRoleInput | null;
  schema_field_values: {[name: string]: Partial<MinimalSchemaTag>};
  parameters: Array<ProcedureParameterValue> | null;
}
interface AnnotateFixedPlateInput {
  annotations: Array<AnnotateFixedPlateWellInput>;
}

export function annotatePlate(
  gridId: string,
  data: AnnotateFixedPlateInput,
  dataModsHeader?: DataModificationsHeader
): Promise<{}> {
  return net.putJSONPromise({url: `/1/api/grids/${gridId}/annotate`, data, extraHeaders: dataModsHeader});
}

export interface PlateMapTransferContentInput {
  entity_id: string;
  final_concentration: MeasurementInput | null;
  material_input: ProcedureParameterValue | null;
  test_orders: Array<PlateMapTestOrderInput> | null;
}

export interface PlateMapTransferSourceContainableInput {
  entity_id: string;
  final_concentration: MeasurementInput | null;
  source_concentration: MeasurementInput | null;
  transfer_quantity: MeasurementInput | null;
  material_input: ProcedureParameterValue | null;
  test_orders: Array<PlateMapTestOrderInput> | null;
}

export interface PlateMapTransferSourceContainerInput {
  container_id: string;
  contents: Array<PlateMapTransferContentInput>;
  transfer_quantity: MeasurementInput | null;
}

export type PlateMapTransferSourceInput =
  | PlateMapTransferSourceContainableInput
  | PlateMapTransferSourceContainerInput;

export interface FixedPlateTransferInputWell {
  position: WellPosition;
  sources: Array<PlateMapTransferSourceInput>;
  contents: Array<PlateMapTransferContentInput>;
  final_quantity: MeasurementInput;
}

export interface FillFixedPlateParams {
  transferInputs: Array<FixedPlateTransferInputWell>;
}

export function fillFixedPlate(
  gridId: string,
  data: FillFixedPlateParams,
  dataModsHeader?: DataModificationsHeader
): Promise<{}> {
  return TaskRunner.postJSONPromise({
    url: `/1/api/grids/${gridId}/fill-fixed-plate`,
    data,
    extraHeaders: dataModsHeader,
  });
}
